import { ISlideInitialData } from '@src/slide/domain/slide-app/core/interfaces/slide-initial-data-service.interface';
import { SaveType } from '@tencent/mega-sdk-protocols';

export interface LoadPresentationOptions {
    globalPadId: string;
    localFilePath: string;
    recoveredType?: string;
    templatePath?: string;
}

export interface SavePresentationOptions {
    globalPadId: string;
    localFilePath: string;
    title: string;
    data: string;
    saveType: SaveType;
    traceKey?: string;
    recoveredType?: string;
}

export interface DataConvertError {
    name: string;
    message: string;
    code?: number | string;
    stack?: string;
}

export enum PasswordError {
    Required = 'required',
    Invalid = 'invalid',
}

export type LoadConvertHandlers = Partial<{
    /**
     * 首页数据回调，在幻灯片首屏数据加赞完成后触发
     *
     * @param totalChunkCount 幻灯片页的总数
     */
    onHead: (totalChunkCount: number) => void;
    /**
     * 分片数据回调，随着导入进程，会逐一返回第二页后的幻灯片数据
     *
     * @param chunkIndex 返回的幻灯片的索引，因为此回调响应第二页之后的数据，因此 chunkIndex >= 1
     * @param totalChunkCount 幻灯片页的总数
     */
    onChunk: (chunkIndex: number, totalChunkCount: number) => void;
    /**
     * 分页导入结束回调，只会在正常结束时触发
     *
     * @param totalChunkCount 幻灯片页的总数
     */
    onEnd: (totalChunkCount: number) => void;
    /**
     * 导入过程发生错误，导入失败
     *
     * @param err 错误详情
     */
    onFatalError: (err: DataConvertError) => void;
    /**
     * 导入的 PPT 文件需要密码或者密码错误需要重新输入
     *
     * @param err 密码错误，包活 PasswordError.Required | PasswordError.Invalid
     * @param code 透传原始错误码，客户端负责密码输入框时需要
     * @param continueWithPassword UI层重新输入密码后，调用 continueWithPassword(newPassword) 继续导入流程
     */
    onPasswordError: (err: PasswordError, code: number,  continueWithPassword: (password: string) => void) => void;
}>;

export type SaveConvertHandlers = Partial<{
    /**
     * PPTX 文档数据已写入
     * @param newPath 写入的文件路径
     * @param newTitle 写入的文档标题
     */
    onDocumentSaved: (newPath?: string, newTitle?: string, newGlobalPadId?: string) => void;
    /**
     * 用户取消此次保存。
     * 场景：另存为时会出现选择文件的弹窗，此时取消选择，则会触发此回调
     */
    onCancel: () => void;
    /**
     * 上次保存未结束时，触发下一次保存，前一次的保存会被终止从而触发 onAbort 回调
     */
    onAbort: () => void;
    /**
     * 导出过程发生错误，导入失败
     *
     * @param err 错误详情
     */
    onFatalError: (err: DataConvertError) => void;
}>;

/**
 * PPTX 导入导出工具封装
 */
export interface ILocalEditDataConverter {
    /**
     * 获取转换工具名称
     */
    readonly name: string
    /**
     * 导入 PPTX
     */
    loadPresentation(options: LoadPresentationOptions, handlers: LoadConvertHandlers): Promise<ISlideInitialData>;
    /**
     * 导出 PPTX
     */
    savePresentation(options: SavePresentationOptions, handlers: SaveConvertHandlers): Promise<void>;
}
